<template>
  <div class="email-suggestion-wrapper">
    <button
      type="button"
      class="email-suggestion-button"
      :class="{ 'is-loading': isLoading, 'is-open': showDropdown }"
      @click="toggleDropdown"
      :disabled="isLoading"
      v-tooltip.top="$t('EMAIL_SUGGESTION.AI_SUGGEST')"
    >
      <svg
        v-if="!isLoading"
        class="suggestion-icon"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M9 21c0 .5.4 1 1 1h4c.6 0 1-.5 1-1v-1H9v1zm3-19C8.1 2 5 5.1 5 9c0 2.4 1.2 4.5 3 5.7V17c0 .5.4 1 1 1h6c.6 0 1-.5 1-1v-2.3c1.8-1.3 3-3.4 3-5.7 0-3.9-3.1-7-7-7z"
          fill="currentColor"
        />
      </svg>
      <spinner v-if="isLoading" size="small" />
    </button>

    <transition name="fade">
      <div v-if="showDropdown" class="dropdown-overlay" @click="closeDropdown"></div>
    </transition>
    
    <transition name="slide-down">
      <div v-if="showDropdown" class="dropdown-menu" ref="dropdownMenu">
        <div class="dropdown-header">
          <span v-if="!isInputMode">{{ $t('EMAIL_SUGGESTION.SELECT_TYPE') }}</span>
          <span v-else>{{ $t('EMAIL_SUGGESTION.ENTER_QUERY') }}</span>
          <button
            type="button"
            class="close-button"
            @click.stop="closeDropdown"
            :title="$t('EMAIL_SUGGESTION.CLOSE')"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 4L4 12M4 4L12 12"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>

        <template v-if="!isInputMode">
          <button
            v-for="type in messageTypes"
            :key="type.value"
            type="button"
            class="dropdown-item"
            @click.stop="handleTypeSelect(type.value)"
          >
            <span class="item-title">{{ type.label }}</span>
          </button>
        </template>

        <template v-else>
          <div class="px-3 py-3">
            <label class="block text-xs font-semibold mb-2">{{ $t('EMAIL_SUGGESTION.PROMPT_LABEL') }}</label>
            <input
              ref="userQueryInput"
              v-model="userQuery"
              type="text"
              class="w-full border rounded-sm px-3 py-2 text-sm"
              :placeholder="$t('EMAIL_SUGGESTION.PROMPT_PLACEHOLDER')"
              :disabled="isLoading"
              @keyup.enter.prevent="submitUserQuery"
            />
            <div class="mt-3 actions-row">
              <div class="selected-type-chip">
                <span class="chip-title">{{ selectedTypeLabel }}</span>
                <button
                  type="button"
                  class="chip-close"
                  :title="$t('EMAIL_SUGGESTION.REMOVE_TYPE')"
                  @click.stop="clearSelectedType"
                >
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </button>
              </div>
              <button
                type="button"
                class="ok-button"
                :disabled="isLoading"
                @click.stop="submitUserQuery"
              >
                <spinner v-if="isLoading" size="small" />
                <span v-else>{{ $t('EMAIL_SUGGESTION.OK') }}</span>
              </button>
            </div>
          </div>
        </template>
      </div>
    </transition>
  </div>
</template>

<script>
/* global axios */
import Spinner from 'shared/components/Spinner.vue';
import alertMixin from 'shared/mixins/alertMixin';
import { mapGetters } from 'vuex';

export default {
  name: 'EmailSuggestionButton',
  components: {
    Spinner,
  },
  mixins: [alertMixin],
  props: {
    contactId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      showDropdown: false,
      isLoading: false,
      isInputMode: false,
      selectedType: null,
      userQuery: '',
    };
  },
  computed: {
    ...mapGetters({
      currentAccountId: 'getCurrentAccountId',
    }),
    messageTypes() {
      return [
        {
          value: 'greet',
          label: 'Greet',
          icon: 'person-wave',
        },
        {
          value: 'reminder',
          label: 'Reminder',
          icon: 'alert',
        },
        {
          value: 'follow-up',
          label: 'Follow-up',
          icon: 'arrow-reply',
        },
        {
          value: 'invitation',
          label: 'Invitation',
          icon: 'mail',
        },
        {
          value: 'general',
          label: 'General',
          icon: 'chat',
        },
        {
          value: 'proposal',
          label: 'Proposal',
          icon: 'document',
        },
        {
          value: 'announcement',
          label: 'Announcement',
          icon: 'megaphone',
        },
        {
          value: 'thank-you',
          label: 'Thank you',
          icon: 'heart',
        },
      ];
    },
    selectedTypeLabel() {
      const found = this.messageTypes.find(t => t.value === this.selectedType);
      return found ? found.label : '';
    },
  },
  mounted() {
    // No initial listeners needed
  },
  beforeDestroy() {
    // Clean up any listeners
    document.removeEventListener('click', this.handleOutsideClick);
  },
  methods: {
    toggleDropdown(event) {
      if (this.isLoading) return;
      event.stopPropagation();
      
      this.showDropdown = !this.showDropdown;
      
      // Add outside click listener when opening
      if (this.showDropdown) {
        this.$nextTick(() => {
          setTimeout(() => {
            document.addEventListener('click', this.handleOutsideClick);
          }, 100);
        });
      } else {
        document.removeEventListener('click', this.handleOutsideClick);
      }
    },
    closeDropdown() {
      this.showDropdown = false;
      document.removeEventListener('click', this.handleOutsideClick);
    },
    handleOutsideClick(event) {
      // Check if click is inside the dropdown menu
      const dropdown = this.$refs.dropdownMenu;
      const button = this.$el.querySelector('.email-suggestion-button');
      
      if (dropdown && !dropdown.contains(event.target) && !button.contains(event.target)) {
        this.closeDropdown();
      }
    },
    handleTypeSelect(messageType) {
      if (this.isLoading) return;
      this.selectedType = messageType;
      this.isInputMode = true;
      this.$nextTick(() => {
        this.$refs.userQueryInput && this.$refs.userQueryInput.focus();
      });
    },

    clearSelectedType() {
      if (this.isLoading) return;
      this.isInputMode = false;
      this.selectedType = null;
      // keep userQuery intact so user can reuse text if they re-select
    },

    async submitUserQuery() {
      if (this.isLoading) return;
      const messageType = this.selectedType;
      if (!messageType) return;

      this.isLoading = true;
      try {
        const response = await axios.post(
          `/api/v1/accounts/${this.currentAccountId}/contacts/${this.contactId}/email_suggestions`,
          { message_type: messageType, user_query: this.userQuery },
          { timeout: 302000 }
        );

        if (response.data) {
          // If backend signals Python failure with success:false, show error and stop
          if (response.data.success === false) {
            const msg = response.data.message || this.$t('EMAIL_SUGGESTION.ERROR');
            this.showAlert(msg);
          } else {
            this.$emit('suggestion-generated', {
              subject: response.data.subject,
              message: response.data.message,
              source: response.data.source,
            });

            const sourceText =
              response.data.source === 'ai_generated'
                ? this.$t('EMAIL_SUGGESTION.AI_GENERATED')
                : this.$t('EMAIL_SUGGESTION.TEMPLATE_BASED');

            this.showAlert(
              this.$t('EMAIL_SUGGESTION.SUCCESS', { source: sourceText })
            );
          }
        }
        // Close and reset
        this.closeDropdown();
        this.isInputMode = false;
        this.selectedType = null;
        this.userQuery = '';
      } catch (error) {
        console.error('Email suggestion error:', error);

        let errorMessage;
        if (error.response?.data?.error === 'No previous conversation found') {
          errorMessage = this.$t('EMAIL_SUGGESTION.NO_CONVERSATION');
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else {
          errorMessage = this.$t('EMAIL_SUGGESTION.ERROR');
        }
        this.showAlert(errorMessage);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.email-suggestion-wrapper {
  position: relative;
  display: inline-block;
}

.email-suggestion-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  background: #1f93ff;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(251, 191, 36, 0.3);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(251, 191, 36, 0.4);
    background: #007ef3;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  &.is-loading {
    background: #007ef3;
  }

  &.is-open {
    background: #007ef3;
  }

  .suggestion-icon {
    display: block;
    color: white;
    animation: pulse 2s ease-in-out infinite;
    flex-shrink: 0;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

// Transition animations
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.slide-down-enter-active {
  animation: slideDown 0.2s ease;
}

.slide-down-leave-active {
  animation: slideUp 0.15s ease;
}

.dropdown-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  z-index: 99999 !important;
  background: rgba(0, 0, 0, 0.4) !important;
  pointer-events: all !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dropdown-menu {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  width: 22rem;
  min-width: 22rem;
  max-height: 32rem;
  background: var(--white);
  border-radius: 0.5rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px var(--s-100);
  z-index: 100000 !important;
  overflow-y: auto;
  pointer-events: all !important;

  :global(body.dark) & {
    background: var(--s-900);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6), 0 0 0 1px var(--s-700);
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}

@keyframes slideUp {
  from {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
  to {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.95);
  }
}

.dropdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 0.875rem;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  color: var(--s-600);
  background: var(--s-50);
  border-bottom: 1px solid var(--s-100);

  :global(body.dark) & {
    background: var(--s-800);
    border-bottom-color: var(--s-700);
    color: var(--s-300);
  }
}

.close-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  background: transparent;
  border: none;
  border-radius: 0.25rem;
  color: var(--s-500);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: var(--s-100);
    color: var(--s-700);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    display: block;
  }

  :global(body.dark) & {
    color: var(--s-400);

    &:hover {
      background: var(--s-700);
      color: var(--s-200);
    }
  }
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.75rem 0.875rem;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;

  &:hover {
    background: var(--s-50);

    .item-icon {
      color: var(--w-500);
      transform: scale(1.1);
    }
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--s-75);
  }

  .item-icon {
    color: var(--s-500);
    flex-shrink: 0;
    transition: all 0.15s ease;
  }

  .item-title {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--s-800);
    white-space: nowrap;
  }

  :global(body.dark) & {
    &:hover {
      background: var(--s-800);
    }

    &:not(:last-child) {
      border-bottom-color: var(--s-700);
    }

    .item-icon {
      color: var(--s-400);
    }

    .item-title {
      color: var(--s-100);
    }
  }
}

// Refine popup styles
.selected-type-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  margin: 0;
  border-radius: 0.25rem;
  background: var(--w-50, #eff6ff);
  color: var(--w-700, #1d4ed8);
  border: 1px solid var(--w-100, #dbeafe);
  font-size: 0.8125rem;
  font-weight: 600;
  width: max-content;
}

.chip-title {
  line-height: 1;
}

.chip-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 9999px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  padding: 0;
}

.chip-close:hover {
  background: var(--w-100, #dbeafe);
}

.ok-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 2rem;
  padding: 0 0.9rem;
  border: none;
  border-radius: 0.375rem;
  background: #1f93ff;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}
.actions-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.ok-button:hover:enabled {
  background: #007ef3;
}

.ok-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>

